import { NoAdminMode, PlanType, WaitingType } from '@app/core/interfaces/c5/enumerations';
import { MeetingMember } from '@app/core/interfaces/c5/meeting-member';
import { MeetingUpdate } from '@app/core/interfaces/portal/meeting-update';
import { MeetingUpdateMember } from '@app/core/interfaces/portal/meeting-update-member';
import { DateUtils } from '@app/utils/date';
import { FormMapping } from '@app/utils/form-mapping/decorators/form-mapping';
import { FormMappingAttribute } from '@app/utils/form-mapping/decorators/form-mapping-attribute';
import {
  BooleanInput,
  IntInput,
  Max,
  Min,
  Required,
  StringInput,
} from '@app/utils/form-mapping/helpers/default-attributes';
import { FormMappingModel } from '@app/utils/form-mapping/models/form-mapping-model';
import { isBlank } from '@app/utils/string';
import { addMinutes, differenceInMinutes } from 'date-fns';

@FormMapping()
export class MeetingForm extends FormMappingModel {
  @StringInput
  adminPassphrase: string;

  @IntInput({
    mergeFunction: (model: MeetingUpdate, value: number, _, form: MeetingForm) => {
      if (form.noAdminTimeout) {
        model.noAdminTimeout = value;
      } else {
        model.noAdminTimeout = undefined;
      }
    },
    parseFunction: (value: number | undefined) => {
      return value ?? 0;
    },
    selector: 'noAdminTimeout',
  })
  adminTimeout: number;

  @BooleanInput
  dialIn: boolean;

  @IntInput({
    mergeFunction: (model: MeetingUpdate, value: string, _, form: MeetingForm) => {
      if (isBlank(value) || value === '0' || form.scheduledMeeting === false) {
        model.end = null;
      } else {
        const duration = parseInt(value);
        model.end = addMinutes(form.start, duration).getTime();
      }
    },
    parseFunction: (_, o: MeetingUpdate) => {
      return o.end != null ? differenceInMinutes(o.end, o.start) : undefined;
    },
  })
  duration: number | undefined;

  @FormMappingAttribute({
    parseFunction: (value: MeetingMember[]) => {
      return value.map((member) => {
        return {
          role: member.memberRole,
          userId: member.userId,
        } as MeetingUpdateMember;
      });
    },
  })
  members: MeetingUpdateMember[];

  @Required
  @StringInput
  name: string;

  @StringInput
  noAdminMode: NoAdminMode;

  @IntInput({
    defaultValue: 0,
    mergeFunction: (model: MeetingUpdate, value: number) => {
      model.noAdminTimeout = value ?? 0;
    },
  })
  @Min(0)
  @Max(100)
  @Required
  noAdminTimeout: number | undefined;

  @BooleanInput({
    defaultValue: false,
  })
  recordName: boolean;

  @BooleanInput({
    mergeFunction: (model: MeetingUpdate, value: boolean) => {
      model.planType = value ? PlanType.SCHEDULED : PlanType.PERMANENT;
    },
    parseFunction: (value: PlanType) => value === PlanType.SCHEDULED,
    selector: 'planType',
  })
  scheduledMeeting: boolean;

  @IntInput
  @Required
  siteId: number;

  @FormMappingAttribute({
    mergeFunction: (model: MeetingUpdate, value: Date) => {
      if (value != null) {
        model.start = value.getTime();
      }
    },
    parseFunction: (value) => (value != null ? DateUtils.parse(value) : value),
  })
  start: Date;

  @StringInput
  uniqueName: string;

  @StringInput
  userPassphrase: string;

  @BooleanInput({
    defaultValue: false,
  })
  videoMode: boolean;

  @StringInput
  waitingType: WaitingType | undefined;
}
